import React, { useState, useEffect } from 'react';
import { FirebasePatientService, Patient } from '../../../services/firebase/FirebasePatientService';
import { firebaseAppointmentService, Appointment } from '../../../services/firebase/FirebaseAppointmentService';
import { useAuth } from '../../../contexts/AuthContext';
import { AuditService, AuditEntry } from '../../../services/firebase/AuditService';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';

type SubTab = 'history' | 'notifications' | 'tasks' | 'requests';

interface ActivityEvent {
  id: string;
  date: string;
  time: string;
  event: string;
  linkedClient?: string;
  linkedAppointment?: string;
  userId?: string;
  userName?: string;
}

interface Notification {
  id: string;
  type: 'birthday' | 'missing-exam' | 'missing-plan' | 'missing-notes' | 'appointment-request' | 'documents-completed';
  title: string;
  description: string;
  client?: string;
  timeAgo: string;
  actionLink?: string;
  isRead: boolean;
}

interface Task {
  id: string;
  name: string;
  description: string;
  dueDate?: Date;
  dueTime?: string;
  priority: 'none' | 'low' | 'medium' | 'high';
  clientId?: string;
  clientName?: string;
  attachments: File[];
  completed: boolean;
  completedDate?: Date;
}

interface AppointmentRequest {
  id: string;
  clientName: string;
  clientAge: number;
  clientType: 'Adult' | 'Minor';
  prospectiveStatus: 'Prospective' | 'Active';
  appointmentDetails: string;
  status: 'pending' | 'accepted' | 'rejected';
  dateReceived: Date;
}

const ActivityTab: React.FC = () => {
  const { user } = useAuth();
  const providerId = user?.uid || '';
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('history');
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Activity History
  const [activityEvents, setActivityEvents] = useState<ActivityEvent[]>([]);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityEventFilter, setActivityEventFilter] = useState('all');
  const [activityTimeFilter, setActivityTimeFilter] = useState('all');
  const [showActivityDetails, setShowActivityDetails] = useState(false);
  const [teamMemberFilter, setTeamMemberFilter] = useState('all');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [notificationSort, setNotificationSort] = useState('by-date');

  // Tasks
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskForm, setTaskForm] = useState<Task>({
    id: '',
    name: '',
    description: '',
    dueDate: undefined,
    dueTime: '',
    priority: 'none',
    clientId: '',
    clientName: '',
    attachments: [],
    completed: false
  });

  // Requests
  const [appointmentRequests, setAppointmentRequests] = useState<AppointmentRequest[]>([]);
  const [requestsTab, setRequestsTab] = useState<'pending' | 'archived'>('pending');
  const [requestSearch, setRequestSearch] = useState('');
  const [requestDateFilter, setRequestDateFilter] = useState('last-90-days');
  const [requestClientStatusFilter, setRequestClientStatusFilter] = useState('all');
  const [requestLocationFilter, setRequestLocationFilter] = useState('all');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');

  const [patients, setPatients] = useState<Patient[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!providerId) return;
    const allPatients = await FirebasePatientService.getAllPatients(providerId);
    setPatients(allPatients);

    // Load team members for name resolution
    const service = TeamMemberService.getInstance();
    const members = service.getAllTeamMembers();
    setTeamMembers(members);

    // Load activity events for ALL team members
    loadActivityEvents(members);

    // Load notifications
    loadNotifications();

    // Load tasks from localStorage
    const savedTasks = localStorage.getItem('provider-tasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }

    // Load appointment requests
    loadAppointmentRequests();
  };

  const loadActivityEvents = async (members?: TeamMember[]) => {
    if (!providerId) return;
    try {
      // Fetch ALL team members' audit entries (not just current user)
      const auditEntries = await AuditService.getAllAuditTrail(200);
      const memberList = members || teamMembers;

      // Build a map of authUid → team member name for display
      const uidToName: Record<string, string> = {};
      memberList.forEach(m => {
        if (m.authUid) uidToName[m.authUid] = m.name;
      });

      const events: ActivityEvent[] = auditEntries.map((entry: AuditEntry) => {
        const ts = entry.timestamp instanceof Date ? entry.timestamp : new Date();
        return {
          id: entry.id || ts.getTime().toString(),
          date: ts.toLocaleDateString('en-US'),
          time: ts.toLocaleTimeString('en-US'),
          event: formatAuditEvent(entry),
          linkedClient: entry.resourceOwner || undefined,
          userId: entry.userId,
          userName: uidToName[entry.userId] || (entry.userId === 'system' ? 'System' : entry.userId?.substring(0, 8) + '...'),
        };
      });
      setActivityEvents(events);
    } catch (error) {
      console.error('[ActivityTab] Error loading audit trail:', error);
      setActivityEvents([]);
    }
  };

  const formatAuditEvent = (entry: AuditEntry): string => {
    const actionLabels: Record<string, string> = {
      view: 'Viewed', create: 'Created', update: 'Updated', delete: 'Deleted',
      export: 'Exported', print: 'Printed', login: 'Logged in', logout: 'Logged out',
      login_failed: 'Failed login attempt', inquiry_created: 'Inquiry created',
      invite_created: 'Invite sent', invite_accepted: 'Invite accepted',
    };
    const resourceLabels: Record<string, string> = {
      patient: 'patient record', appointment: 'appointment', note: 'clinical note',
      psychotherapy_note: 'psychotherapy note', form: 'form', recording: 'recording',
      session: 'session', auth: 'account', inquiry: 'inquiry', invite: 'invite',
    };
    const action = actionLabels[entry.action] || entry.action;
    const resource = resourceLabels[entry.resourceType] || entry.resourceType;
    const detail = entry.details ? ` — ${entry.details}` : '';
    return `${action} ${resource}${detail}`;
  };

  const loadNotifications = () => {
    // TODO: Load notifications from Firestore
    setNotifications([]);
  };

  const loadAppointmentRequests = () => {
    // Mock appointment requests
    const mockRequests: AppointmentRequest[] = [];
    setAppointmentRequests(mockRequests);
  };

  const handleAddTask = () => {
    const newTask: Task = {
      ...taskForm,
      id: Date.now().toString(),
      completed: false
    };
    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);
    localStorage.setItem('provider-tasks', JSON.stringify(updatedTasks));

    // Add to activity log
    logActivity(`You created a new task: "${newTask.name}"`);

    setShowAddTaskModal(false);
    resetTaskForm();
  };

  const handleToggleTaskComplete = (taskId: string) => {
    const updatedTasks = tasks.map(task => {
      if (task.id === taskId) {
        const completed = !task.completed;
        const completedDate = completed ? new Date() : undefined;

        // Log activity
        if (completed) {
          logActivity(`You completed task: "${task.name}"`);
        } else {
          logActivity(`You marked task as incomplete: "${task.name}"`);
        }

        return { ...task, completed, completedDate };
      }
      return task;
    });
    setTasks(updatedTasks);
    localStorage.setItem('provider-tasks', JSON.stringify(updatedTasks));
  };

  const logActivity = (eventText: string) => {
    const now = new Date();
    const newEvent: ActivityEvent = {
      id: Date.now().toString(),
      date: now.toLocaleDateString('en-US'),
      time: now.toLocaleTimeString('en-US'),
      event: eventText
    };
    const updatedEvents = [newEvent, ...activityEvents];
    setActivityEvents(updatedEvents);
    // In production, save to localStorage or send to backend
  };

  const resetTaskForm = () => {
    setTaskForm({
      id: '',
      name: '',
      description: '',
      dueDate: undefined,
      dueTime: '',
      priority: 'none',
      clientId: '',
      clientName: '',
      attachments: [],
      completed: false
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'birthday': return '🎂';
      case 'missing-exam': return '🧠';
      case 'missing-plan': return '📋';
      case 'missing-notes': return '📝';
      case 'appointment-request': return '📅';
      case 'documents-completed': return '✉️';
      default: return '📬';
    }
  };

  const groupNotificationsByTime = () => {
    const today: Notification[] = [];
    const last7Days: Notification[] = [];
    const last30Days: Notification[] = [];
    const older: Notification[] = [];

    notifications.forEach(notif => {
      const timeAgo = notif.timeAgo;
      if (timeAgo.includes('h') || timeAgo.includes('m')) {
        today.push(notif);
      } else if (timeAgo.includes('d') && parseInt(timeAgo) <= 7) {
        last7Days.push(notif);
      } else if (timeAgo.includes('w') && parseInt(timeAgo) <= 4) {
        last30Days.push(notif);
      } else {
        older.push(notif);
      }
    });

    return { today, last7Days, last30Days, older };
  };

  const getRequestStats = () => {
    return {
      prospectiveClients: 0,
      activeClients: 0,
      expiringSoon: 0,
      documentsIncomplete: 0,
      documentsCompleted: 0
    };
  };

  return (
    <div style={{
      padding: '32px',
      backgroundColor: '#16325B',
      minHeight: '100vh',
      color: '#f9fafb'
    }}>
      {/* Header */}
      <h1 style={{
        fontSize: '32px',
        fontWeight: '600',
        color: '#f9fafb',
        margin: '0 0 24px 0'
      }}>
        {activeSubTab === 'history' && 'Team Activity'}
        {activeSubTab === 'notifications' && 'Notifications'}
        {activeSubTab === 'tasks' && 'Tasks'}
        {activeSubTab === 'requests' && 'Request Management'}
      </h1>

      {/* Sub Tabs */}
      <div style={{
        borderBottom: '2px solid #374151',
        marginBottom: '24px',
        display: 'flex',
        gap: '32px'
      }}>
        <button
          onClick={() => setActiveSubTab('history')}
          style={{
            padding: '12px 0',
            background: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'history' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeSubTab === 'history' ? '#3b82f6' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '-2px'
          }}
        >
          History
        </button>
        <button
          onClick={() => setActiveSubTab('notifications')}
          style={{
            padding: '12px 0',
            background: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'notifications' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeSubTab === 'notifications' ? '#3b82f6' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '-2px'
          }}
        >
          Notifications
        </button>
        <button
          onClick={() => setActiveSubTab('tasks')}
          style={{
            padding: '12px 0',
            background: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'tasks' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeSubTab === 'tasks' ? '#3b82f6' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '-2px'
          }}
        >
          Tasks
        </button>
        <button
          onClick={() => setActiveSubTab('requests')}
          style={{
            padding: '12px 0',
            background: 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'requests' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeSubTab === 'requests' ? '#3b82f6' : '#9ca3af',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '-2px'
          }}
        >
          Request Management
        </button>
      </div>

      {/* Activity History Content */}
      {activeSubTab === 'history' && (() => {
        // Filter events by team member and search
        const filteredEvents = activityEvents.filter(event => {
          if (teamMemberFilter !== 'all' && event.userId !== teamMemberFilter) return false;
          if (activitySearch) {
            const term = activitySearch.toLowerCase();
            return (
              event.event.toLowerCase().includes(term) ||
              (event.userName || '').toLowerCase().includes(term) ||
              (event.linkedClient || '').toLowerCase().includes(term)
            );
          }
          return true;
        });

        // Get unique team member names for filter dropdown
        const uniqueMembers = Array.from(
          new Map(activityEvents.filter(e => e.userId).map(e => [e.userId, e.userName])).entries()
        );

        return (
        <div>
          {/* Search and Filters */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            flexWrap: 'wrap'
          }}>
            <input
              type="text"
              placeholder="Search events or team members..."
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
              style={{
                flex: '1',
                minWidth: '200px',
                maxWidth: '300px',
                padding: '10px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            />

            <select
              value={teamMemberFilter}
              onChange={(e) => setTeamMemberFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Team Members</option>
              {uniqueMembers.map(([uid, name]) => (
                <option key={uid} value={uid}>{name}</option>
              ))}
            </select>

            <select
              value={activityEventFilter}
              onChange={(e) => setActivityEventFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Events</option>
              <option value="appointments">Appointments</option>
              <option value="calendar">Calendar Events</option>
              <option value="client-actions">Client Actions</option>
              <option value="tasks">Tasks</option>
            </select>

            <button
              onClick={() => setShowActivityDetails(!showActivityDetails)}
              style={{
                marginLeft: 'auto',
                padding: '10px 16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#3b82f6',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              {showActivityDetails ? 'Hide details' : 'Show details'}
            </button>
          </div>

          {/* Summary bar */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#9ca3af'
          }}>
            <span>Showing {filteredEvents.length} of {activityEvents.length} events</span>
            {teamMemberFilter !== 'all' && (
              <button
                onClick={() => setTeamMemberFilter('all')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#D4A017',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textDecoration: 'underline'
                }}
              >
                Clear filter
              </button>
            )}
          </div>

          {/* Activity Table */}
          <div style={{
            backgroundColor: '#28527A',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '120px 100px 180px 1fr',
              gap: '16px',
              padding: '16px',
              backgroundColor: '#1e3a5f',
              borderBottom: '1px solid #374151',
              fontWeight: '600',
              fontSize: '14px',
              color: '#d1d5db'
            }}>
              <div>Date</div>
              <div>Time</div>
              <div>Team Member</div>
              <div>Event</div>
            </div>

            {/* Table Body */}
            {filteredEvents.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af' }}>
                No activity events found.
              </div>
            ) : (
              filteredEvents.map((event) => (
                <div
                  key={event.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 100px 180px 1fr',
                    gap: '16px',
                    padding: '16px',
                    borderBottom: '1px solid #374151',
                    alignItems: 'flex-start'
                  }}
                >
                  <div style={{ color: '#e5e7eb', fontSize: '13px' }}>{event.date}</div>
                  <div style={{ color: '#e5e7eb', fontSize: '13px' }}>{event.time}</div>
                  <div style={{ color: '#F7E9A0', fontSize: '13px', fontWeight: '500' }}>
                    {event.userName || 'Unknown'}
                  </div>
                  <div style={{ color: '#e5e7eb', fontSize: '13px' }}>
                    {event.event}{' '}
                    {event.linkedClient && (
                      <span style={{ color: '#3b82f6' }}>
                        {event.linkedClient}
                      </span>
                    )}
                    {event.linkedAppointment && (
                      <span style={{ color: '#3b82f6' }}>
                        {event.linkedAppointment}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        );
      })()}

      {/* Notifications Content */}
      {activeSubTab === 'notifications' && (
        <div>
          {/* Filters */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            alignItems: 'center'
          }}>
            <select
              value={notificationFilter}
              onChange={(e) => setNotificationFilter(e.target.value)}
              style={{
                padding: '10px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="all">All</option>
              <option value="birthdays">Birthdays</option>
              <option value="missing-items">Missing Items</option>
              <option value="appointments">Appointments</option>
            </select>

            <select
              value={notificationSort}
              onChange={(e) => setNotificationSort(e.target.value)}
              style={{
                padding: '10px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="by-date">By date</option>
              <option value="by-priority">By priority</option>
              <option value="by-client">By client</option>
            </select>

            <button
              onClick={() => {
                setNotifications(notifications.map(n => ({ ...n, isRead: true })));
              }}
              style={{
                marginLeft: 'auto',
                padding: '10px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Mark all as read
            </button>

            <button
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#d1d5db',
                fontSize: '18px',
                cursor: 'pointer'
              }}
            >
              ⚙️
            </button>
          </div>

          {/* Grouped Notifications */}
          <div>
            {(() => {
              const grouped = groupNotificationsByTime();
              return (
                <>
                  {grouped.today.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                      <h3 style={{ fontSize: '16px', color: '#d1d5db', marginBottom: '16px', fontWeight: '500' }}>
                        Today
                      </h3>
                      {grouped.today.map(notif => (
                        <div
                          key={notif.id}
                          style={{
                            backgroundColor: '#dbeafe',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '16px'
                          }}
                        >
                          <div style={{ fontSize: '32px' }}>{getNotificationIcon(notif.type)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                              {notif.title}
                            </div>
                            <div style={{ color: '#1e40af', fontSize: '14px' }}>
                              {notif.description}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>{notif.timeAgo}</span>
                            <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
                              ⋯
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {grouped.last7Days.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                      <h3 style={{ fontSize: '16px', color: '#d1d5db', marginBottom: '16px', fontWeight: '500' }}>
                        Last 7 days
                      </h3>
                      {grouped.last7Days.map(notif => (
                        <div
                          key={notif.id}
                          style={{
                            backgroundColor: '#dbeafe',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '16px'
                          }}
                        >
                          <div style={{ fontSize: '32px' }}>{getNotificationIcon(notif.type)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                              {notif.title}
                            </div>
                            <div style={{ color: '#1e40af', fontSize: '14px' }}>
                              {notif.description}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>{notif.timeAgo}</span>
                            <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
                              ⋯
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {grouped.last30Days.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                      <h3 style={{ fontSize: '16px', color: '#d1d5db', marginBottom: '16px', fontWeight: '500' }}>
                        Last 30 days
                      </h3>
                      {grouped.last30Days.map(notif => (
                        <div
                          key={notif.id}
                          style={{
                            backgroundColor: notif.type === 'missing-exam' || notif.type === 'missing-plan' || notif.type === 'missing-notes'
                              ? '#fed7d7' : '#fef3c7',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '16px'
                          }}
                        >
                          <div style={{ fontSize: '32px' }}>{getNotificationIcon(notif.type)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              color: notif.type === 'missing-exam' || notif.type === 'missing-plan' || notif.type === 'missing-notes'
                                ? '#991b1b' : '#92400e',
                              marginBottom: '4px'
                            }}>
                              {notif.title}
                            </div>
                            <div style={{
                              color: notif.type === 'missing-exam' || notif.type === 'missing-plan' || notif.type === 'missing-notes'
                                ? '#991b1b' : '#92400e',
                              fontSize: '14px'
                            }}>
                              {notif.description}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>{notif.timeAgo}</span>
                            <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
                              ⋯
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Tasks Content */}
      {activeSubTab === 'tasks' && (
        <div>
          {tasks.length === 0 ? (
            /* Empty State */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '120px', marginBottom: '24px' }}>👤💬</div>
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px', color: '#f9fafb' }}>
                Stay organized with Tasks
              </h2>
              <p style={{ fontSize: '16px', color: '#d1d5db', marginBottom: '32px', maxWidth: '500px' }}>
                Keep track of your to-dos by creating tasks, setting due dates, and assigning priority levels
              </p>
              <button
                onClick={() => setShowAddTaskModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Add task
              </button>
            </div>
          ) : (
            /* Task List */
            <div>
              <button
                onClick={() => setShowAddTaskModal(true)}
                style={{
                  marginBottom: '24px',
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Add task
              </button>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tasks.map(task => (
                  <div
                    key={task.id}
                    style={{
                      backgroundColor: '#28527A',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      opacity: task.completed ? 0.6 : 1
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => handleToggleTaskComplete(task.id)}
                      style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '600',
                        color: '#f9fafb',
                        marginBottom: '4px',
                        textDecoration: task.completed ? 'line-through' : 'none'
                      }}>
                        {task.name}
                      </div>
                      {task.description && (
                        <div style={{ fontSize: '14px', color: '#d1d5db', marginBottom: '8px' }}>
                          {task.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#d1d5db' }}>
                        {task.dueDate && (
                          <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                        )}
                        {task.priority !== 'none' && (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: task.priority === 'high' ? '#dc2626' :
                              task.priority === 'medium' ? '#f59e0b' : '#6b7280',
                            color: 'white'
                          }}>
                            {task.priority}
                          </span>
                        )}
                        {task.clientName && (
                          <span>Client: {task.clientName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Request Management Content */}
      {activeSubTab === 'requests' && (
        <div>
          {/* Sub-tabs */}
          <div style={{
            borderBottom: '1px solid #374151',
            marginBottom: '24px',
            display: 'flex',
            gap: '24px'
          }}>
            <button
              onClick={() => setRequestsTab('pending')}
              style={{
                padding: '12px 0',
                background: 'transparent',
                border: 'none',
                borderBottom: requestsTab === 'pending' ? '2px solid #3b82f6' : '2px solid transparent',
                color: requestsTab === 'pending' ? '#3b82f6' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '-1px'
              }}
            >
              Pending requests (0)
            </button>
            <button
              onClick={() => setRequestsTab('archived')}
              style={{
                padding: '12px 0',
                background: 'transparent',
                border: 'none',
                borderBottom: requestsTab === 'archived' ? '2px solid #3b82f6' : '2px solid transparent',
                color: requestsTab === 'archived' ? '#3b82f6' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '-1px'
              }}
            >
              Archived requests
            </button>
          </div>

          {requestsTab === 'pending' && (
            <>
              {/* Stats Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                {[
                  { label: 'Prospective clients', count: 0, link: 'View' },
                  { label: 'Active clients', count: 0, link: 'View' },
                  { label: 'Expiring soon', count: 0, link: 'View' },
                  { label: 'Documents incomplete', count: 0, link: 'View' },
                  { label: 'Documents completed', count: 0, link: 'View' }
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: '#28527A',
                      borderRadius: '8px',
                      padding: '20px',
                      border: '1px solid #6CA6C1'
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#d1d5db', marginBottom: '8px' }}>
                      {stat.label}
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: '600', color: '#f9fafb', marginBottom: '8px' }}>
                      {stat.count}
                    </div>
                    <span style={{ fontSize: '14px', color: '#3b82f6', cursor: 'pointer' }}>
                      {stat.link}
                    </span>
                  </div>
                ))}
              </div>

              {/* Filters */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px',
                flexWrap: 'wrap'
              }}>
                <select
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option>≡ All client statuses</option>
                  <option>Prospective</option>
                  <option>Active</option>
                </select>

                <select
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option>≡ All locations</option>
                  <option>Video</option>
                  <option>In-Person</option>
                </select>

                <select
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option>≡ All request sources</option>
                  <option>Client Portal</option>
                  <option>Phone</option>
                  <option>Email</option>
                </select>

                <select
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option>≡ All document statuses</option>
                  <option>Complete</option>
                  <option>Incomplete</option>
                </select>

                <div style={{
                  marginLeft: 'auto',
                  padding: '10px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#d1d5db',
                  fontSize: '14px'
                }}>
                  Expiring soon
                </div>
              </div>

              {/* Empty State */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '80px 20px',
                textAlign: 'center',
                backgroundColor: '#28527A',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '120px', marginBottom: '24px' }}>🧘</div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#f9fafb' }}>
                  You're all caught up!
                </h3>
                <p style={{ fontSize: '16px', color: '#d1d5db' }}>
                  We'll let you know when there are new appointment requests
                </p>
              </div>
            </>
          )}

          {requestsTab === 'archived' && (
            <>
              {/* Search and Filters */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px',
                flexWrap: 'wrap'
              }}>
                <input
                  type="text"
                  placeholder="Search by client name"
                  style={{
                    flex: '1',
                    minWidth: '200px',
                    maxWidth: '300px',
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px'
                  }}
                />

                <button
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  📅 Last 90 days
                </button>

                <select
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option>≡ All client statuses</option>
                </select>

                <select
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option>≡ All locations</option>
                </select>

                <select
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option>≡ All statuses</option>
                </select>
              </div>

              {/* Table */}
              <div style={{
                backgroundColor: '#28527A',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 2fr 120px 150px',
                  gap: '16px',
                  padding: '16px',
                  backgroundColor: '#1e3a5f',
                  borderBottom: '1px solid #374151',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#d1d5db'
                }}>
                  <div>Client</div>
                  <div>Appointment details</div>
                  <div>Status</div>
                  <div>Date received</div>
                </div>

                {/* Empty Message */}
                <div style={{
                  padding: '48px',
                  textAlign: 'center',
                  color: '#d1d5db',
                  fontSize: '14px'
                }}>
                  No archived requests found
                </div>
              </div>

              <p style={{ fontSize: '12px', color: '#d1d5db', marginTop: '16px' }}>
                Archived requests will be automatically removed after 90 days
              </p>
            </>
          )}
        </div>
      )}

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                margin: 0,
                color: '#1e293b',
                fontSize: '24px',
                fontWeight: '600'
              }}>
                Create task
              </h2>
              <button
                onClick={() => {
                  setShowAddTaskModal(false);
                  resetTaskForm();
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                  Task name
                </label>
                <input
                  type="text"
                  placeholder="Write a task"
                  value={taskForm.name}
                  onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#f8fafc',
                    color: '#1e293b',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                  Description
                </label>
                <textarea
                  placeholder="Add task description"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#f8fafc',
                    color: '#1e293b',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Due on
                  </label>
                  <input
                    type="date"
                    value={taskForm.dueDate ? new Date(taskForm.dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value ? new Date(e.target.value) : undefined })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Time
                  </label>
                  <input
                    type="time"
                    value={taskForm.dueTime}
                    onChange={(e) => setTaskForm({ ...taskForm, dueTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                  Priority
                </label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as any })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#f8fafc',
                    color: '#1e293b',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                  Client
                </label>
                <select
                  value={taskForm.clientId}
                  onChange={(e) => {
                    const patient = patients.find(p => p.id === e.target.value);
                    setTaskForm({
                      ...taskForm,
                      clientId: e.target.value,
                      clientName: patient ? `${patient.firstName} ${patient.lastName}` : ''
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#f8fafc',
                    color: '#1e293b',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select client</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                  Attachments
                </label>
                <div style={{
                  border: '2px dashed #cbd5e1',
                  borderRadius: '6px',
                  padding: '24px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: '14px'
                }}>
                  <div style={{ marginBottom: '8px' }}>☁️</div>
                  <div>
                    <button type="button" style={{ color: '#3b82f6', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>Choose file</button> or drag and drop file
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    Max number of attachments: 20<br />
                    Max upload size: 50MB
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '32px' }}>
              <button
                onClick={() => {
                  setShowAddTaskModal(false);
                  resetTaskForm();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddTask}
                disabled={!taskForm.name}
                style={{
                  padding: '12px 24px',
                  backgroundColor: taskForm.name ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: taskForm.name ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
